'use client';

// Third Party
import * as React from 'react';

// Utils
import { useQuery } from '@apollo/client/react';
import {
  ListingReviewsListAllForListingQuery,
  ListingReview,
  ListingReviewSortOptions,
  ListingReviewSummariesListAllForListingQuery,
  ListingReviewsListAllForListingDocument,
  ListingReviewSummariesListAllForListingDocument,
} from '@gql';
import { useToggle } from '@hooks';
export type ReviewsContext = {
  /** Activate the reviews modal */
  activateReviewsModal: VoidFunction;
  /** Deactivate the reviews modal */
  deactivateReviewsModal: VoidFunction;
  /** Flag indicating whether or not the reviews modal is active */
  reviewsModalActive: boolean;
  /** Reviews results data */
  listingReviewsData?: ListingReviewsListAllForListingQuery;
  /** Reviews data loading flag */
  reviewsLoading: boolean;
  /** Current page number */
  page: number;
  /** Set the current page number */
  setPage: (page: number) => void;
  /** All accumulated reviews for infinite scroll */
  allReviews: ListingReview[];
  /** Load more reviews for infinite scroll */
  loadMoreReviews: () => void;
  /** Whether there are more reviews to load */
  hasMoreReviews: boolean;
  /** Reset reviews when modal opens */
  resetReviews: () => void;
  /** Search value for filtering reviews */
  searchValue: string;
  /** Set the search value */
  setSearchValue: (value: string) => void;
  /** Current sort value */
  sortValue: ListingReviewSortOptions | null;
  /** Set the sort value */
  setSortValue: (value: ListingReviewSortOptions | null) => void;
  /** Listing review summaries data */
  listingReviewSummariesData?: ListingReviewSummariesListAllForListingQuery;
  /** Listing review summaries data loading flag */
  listingReviewSummariesLoading: boolean;
};

const ReviewsContext = React.createContext<ReviewsContext | undefined>(
  undefined
);

type ReviewsProviderProps = {
  children: React.ReactNode;
  listingId: string;
};

export const ReviewsProvider: React.FC<ReviewsProviderProps> = ({
  children,
  listingId,
}) => {
  const {
    on: reviewsModalActive,
    setOn: activateReviewsModal,
    setOff: deactivateReviewsModal,
  } = useToggle(false);

  const [page, setPage] = React.useState(1);
  const [allReviews, setAllReviews] = React.useState<ListingReview[]>([]);
  const [hasMoreReviews, setHasMoreReviews] = React.useState(true);
  const [searchValue, setSearchValue] = React.useState('');
  const [sortValue, setSortValue] =
    React.useState<ListingReviewSortOptions | null>(
      ListingReviewSortOptions.DateNewestFirst
    );

  const { data: listingReviewsData, loading: reviewsLoading } = useQuery(
    ListingReviewsListAllForListingDocument,
    {
      variables: {
        id: listingId,
        page: page,
        pageSize: 20,
        sort: sortValue ?? undefined,
        searchQuery: searchValue.trim() ? searchValue : undefined,
      },
    }
  );

  const {
    data: listingReviewSummariesData,
    loading: listingReviewSummariesLoading,
  } = useQuery(ListingReviewSummariesListAllForListingDocument, {
    variables: {
      id: listingId,
    },
  });

  // Track previous sort/search values to detect changes
  const prevSortValue = React.useRef(sortValue);
  const prevSearchValue = React.useRef(searchValue);

  // Reset reviews when modal opens or sort changes
  const resetReviews = React.useCallback(() => {
    setAllReviews([]);
    setPage(1);
    setHasMoreReviews(true);
  }, []);

  // Detect when search or sort changes and reset page to 1
  React.useEffect(() => {
    if (
      prevSortValue.current !== sortValue ||
      prevSearchValue.current !== searchValue
    ) {
      // Reset page to 1 when filters change
      setPage(1);
      setHasMoreReviews(true);
      prevSortValue.current = sortValue;
      prevSearchValue.current = searchValue;
    }
  }, [sortValue, searchValue]);

  // Load more reviews for infinite scroll
  const loadMoreReviews = React.useCallback(() => {
    if (!reviewsLoading && hasMoreReviews) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [reviewsLoading, hasMoreReviews]);

  // Accumulate reviews when new data arrives
  React.useEffect(() => {
    if (
      listingReviewsData?.listingReviewsListAllForListing?.__typename ===
      'ListingReviewCollection'
    ) {
      const newReviews =
        listingReviewsData.listingReviewsListAllForListing.entries;
      const totalReviews =
        listingReviewsData.listingReviewsListAllForListing.meta.totalEntries ||
        0;

      setAllReviews((prevReviews) => {
        const updatedReviews =
          page === 1 ? newReviews : [...prevReviews, ...newReviews];

        setHasMoreReviews(updatedReviews.length < totalReviews);

        return updatedReviews;
      });
    }
  }, [listingReviewsData, page, searchValue]);

  const contextValue: ReviewsContext = {
    activateReviewsModal,
    deactivateReviewsModal,
    reviewsModalActive,
    listingReviewsData,
    reviewsLoading,
    page,
    setPage,
    allReviews,
    loadMoreReviews,
    hasMoreReviews,
    resetReviews,
    searchValue,
    setSearchValue,
    sortValue,
    setSortValue,
    listingReviewSummariesData,
    listingReviewSummariesLoading,
  };

  return (
    <ReviewsContext.Provider value={contextValue}>
      {children}
    </ReviewsContext.Provider>
  );
};

export const useReviews = () => {
  const context = React.useContext(ReviewsContext);

  if (context === undefined) {
    throw new Error('useReviews must be used within a ReviewsProvider');
  }

  return context;
};
